import { Controller } from 'stimulus';

const PLACEHOLDER_DELAY_MS = 250;

export default class extends Controller {
  static targets = ['header'];

  connect() {
    if (this.#loading) {
      this.placeholderTimer = setTimeout(() => this.#show(), PLACEHOLDER_DELAY_MS);
    } else {
      this.#show();
    }
  }

  disconnect() {
    clearTimeout(this.placeholderTimer);
    this.headerObserver?.disconnect();
  }

  headerTargetConnected(header) {
    this.headerObserver ??= new ResizeObserver(([entry]) =>
      this.#publishHeaderHeight(entry.target)
    );
    this.headerObserver.observe(header);
  }

  headerTargetDisconnected(header) {
    this.headerObserver?.unobserve(header);
  }

  loaded(event) {
    if (event.target === this.#panel) {
      this.#show();
    }
  }

  close() {
    this.element.close();
  }

  dismiss() {
    if (this.#dismissible) {
      this.close();
    }
  }

  cancel(event) {
    if (!this.#dismissible) {
      event.preventDefault();
    }
  }

  lock() {
    this.#setDismissible(false);
    this.element.focus();
  }

  unlock() {
    this.#setDismissible(true);
  }

  discard() {
    this.element.remove();
  }

  backdropMousedown(event) {
    this.fromBackdrop = event.target === this.element;
  }

  // A modal that opens between the two clicks of a double click (a fast fetch behind the first
  // click) would otherwise take the second one on its backdrop and close right away.
  backdropClick(event) {
    const fromBackdrop = this.fromBackdrop;
    this.fromBackdrop = false;

    if (fromBackdrop && event.target === this.element && event.detail <= 1) {
      this.dismiss();
    }
  }

  teardown() {
    this.dispatch('close', { bubbles: true });
    this.element.remove();
  }

  get #dismissible() {
    return this.#flags?.dataset.dismissible !== 'false';
  }

  #setDismissible(value) {
    if (this.#flags) {
      this.#flags.dataset.dismissible = value;
    }
  }

  get #flags() {
    return this.#panel?.querySelector(':scope > [data-dismissible]');
  }

  #publishHeaderHeight(header) {
    this.element.style.setProperty('--twin-turbo-modal-header-height', `${header.offsetHeight}px`);
  }

  get #panel() {
    return this.element.querySelector(':scope > turbo-frame');
  }

  get #loading() {
    const panel = this.#panel;

    return !!panel?.src && !panel.hasAttribute('complete');
  }

  #show() {
    clearTimeout(this.placeholderTimer);

    if (!this.element.isConnected) {
      return;
    }

    if (this.element.matches(':modal')) {
      this.element.querySelector('[autofocus]')?.focus();
    } else {
      this.element.showModal();

      if (!this.element.querySelector('[autofocus]')) {
        this.element.focus();
      }
    }
  }
}
